import {
    STORE_CLOSE_CLASS,
    STORE_IS_OPEN_CLASS,
    STORE_OPEN_SOON_CLASS,
    getStoreIsOpenText,
    getStoreCloseText,
    getStoreOpenOrderClosedText,
    getStoreOpensAtText,
    getStoreClosedOpensOnText,
    getStoreOrderOpenOpensAtText,
    getWeek
} from '@/config/statusStore'
import { storeIsOpen } from '@/event/storeIsOpen'
import { configDeliveryTime } from '@/storage/setting/configDeliveryTime'
import { configOpeningHours } from '@/storage/setting/configOpeningHours'
import { configWebOrder } from '@/storage/setting/configWebOrder'
import type { HoursType } from '@/storage/stores'
import stores from '@/storage/stores'
import { getNextDayOpening } from '@/utils/store/getNextDayOpening'
import { getMinutesByData } from '@/utils/time/getMinutesByData'
import { ref } from 'vue'

interface statusStoreIsEmpty {
    class: string
    text: string
}

export const statusStore = ref<statusStoreIsEmpty>({
    class: '',
    text: ''
})

let intervalId: number | undefined

export function getStatusStoreHappy() {
    const arrayPlanning = ref<HoursType[]>([])
    const store = stores.getStoresData()
    const openingHours = configOpeningHours.value
    const forwardOrdering = configDeliveryTime.value?.forward_ordering
    const instantOrdering = configDeliveryTime.value?.instant_ordering
    let nextOpeningTime: number | string | null = ''
    let nextOpeningTimeNumber: number | null = null

    // Store not yet loaded — normal during first mount/route change.
    // Keep default "closed" state and silently wait for next tick.
    if (!store.name) {
        storeIsOpen.value = false
        statusStore.value = {
            class: STORE_CLOSE_CLASS,
            text: getStoreCloseText()
        }
        return
    }

    // Store is loaded but config_opening_hours setting is missing/empty.
    // This IS an anomaly — log as warning so we notice during dev.
    const openingHoursMissing =
        !configOpeningHours.value ||
        (Array.isArray(configOpeningHours.value) &&
            configOpeningHours.value.length === 0)

    if (openingHoursMissing) {
        console.warn(
            '[store] config_opening_hours missing on loaded store:',
            store.name
        )
        storeIsOpen.value = false
        statusStore.value = {
            class: STORE_CLOSE_CLASS,
            text: getStoreCloseText()
        }
        return
    }

    if (!forwardOrdering && (!instantOrdering || instantOrdering === '0')) {
        console.error('no forwardOrdering or instantOrdering')
        storeIsOpen.value = false
        statusStore.value = {
            class: STORE_CLOSE_CLASS,
            text: getStoreCloseText()
        }
        return
    }

    arrayPlanning.value =
        openingHours && openingHours.pickup_service.length > 0
            ? openingHours.pickup_service
            : openingHours.opening_hours

    if (Number(configWebOrder.value) !== 0) {
        const today = new Date()
        const currentDay = { value: today.getUTCDay() || 7 }

        const currentHour = today.getHours()
        const currentMinutes = today.getMinutes()
        const currentTime = getMinutesByData(`${currentHour}:${currentMinutes}`)

        const sortedPlanning = [...arrayPlanning.value].sort((a, b) => {
            const [aHour] = a.open.split(':').map(Number)
            const [bHour] = b.open.split(':').map(Number)
            return aHour - bHour
        })

        const daysData = sortedPlanning.map((p) => p.days).flat()
        const uniqueDaysData = [...new Set(daysData.sort())]

        if (
            !uniqueDaysData.includes(currentDay.value.toString()) &&
            sortedPlanning.length > 0
        ) {
            storeIsOpen.value = false
            statusStore.value = {
                class: STORE_CLOSE_CLASS,
                text: getStoreCloseText()
            }
            return
        } else {
            const currentDayPlanning = sortedPlanning.filter((sp) =>
                sp.days.includes(String(currentDay.value))
            )
            let twoParts = false
            if (currentDayPlanning.length > 1) {
                twoParts = true
            }

            const nextOpeningPeriod = currentDayPlanning.find(
                (sp) => currentTime < getMinutesByData(sp.open)
            )

            const isInsideOpeningHours = currentDayPlanning.some((sp) => {
                const openTime = getMinutesByData(sp.open)
                const closeTime = getMinutesByData(sp.close)
            
                return currentTime >= openTime && currentTime <= closeTime
            })
            const isOutsideOpeningHours = !isInsideOpeningHours

            const nextDayOpening = getNextDayOpening(sortedPlanning, currentDay, null)
            const nextOpeningDayName = getWeek()[currentDay.value % 7]

            nextOpeningTime = nextDayOpening ? nextDayOpening.open : ''
            nextOpeningTimeNumber = nextDayOpening
                ? getMinutesByData(nextDayOpening.open)
                : null

            // const textNextOpen = getStoreClosedOpensOnText(nextOpeningDayName, nextOpeningTime)

            const lastPeriod = currentDayPlanning[currentDayPlanning.length - 1]
            const closeTimeDay = getMinutesByData(lastPeriod.close)

            if (currentDayPlanning.length === 0) {
                storeIsOpen.value = false
                statusStore.value = {
                    class: STORE_CLOSE_CLASS,
                    text: getStoreCloseText()
                }
                return
            }

            if (instantOrdering === '1' && !forwardOrdering) {
                if (isInsideOpeningHours) {
                    storeIsOpen.value = true
                    statusStore.value = {
                        class: STORE_IS_OPEN_CLASS,
                        text: getStoreIsOpenText()
                    }
                    return
                } else if (isOutsideOpeningHours) {
                    storeIsOpen.value = false
                    if (nextOpeningPeriod) {
                        statusStore.value = {
                            class: STORE_OPEN_SOON_CLASS,
                            text: getStoreOpensAtText(nextOpeningPeriod.open)
                        }
                        return
                    } else if (nextDayOpening) {
                        statusStore.value = {
                            class: STORE_CLOSE_CLASS,
                            text:
                                getStoreClosedOpensOnText(nextOpeningDayName, nextOpeningTime)
                        }
                        return
                    }

                    statusStore.value = {
                        class: STORE_CLOSE_CLASS,
                        text: getStoreCloseText()
                    }
                    return
                }
            } else if ((instantOrdering === '0' || !instantOrdering) && forwardOrdering) {
                if (nextOpeningPeriod) {
                    nextOpeningTimeNumber = getMinutesByData(
                        nextOpeningPeriod.open
                    )
                    nextOpeningTime = nextOpeningPeriod.open
                } else if (nextDayOpening) {
                    nextOpeningTime = nextDayOpening.open
                    nextOpeningTimeNumber = getMinutesByData(
                        nextDayOpening.open
                    )
                } else {
                    nextOpeningTime = null
                    nextOpeningTimeNumber = null
                }
                const textNextOpen = getStoreClosedOpensOnText(nextOpeningDayName, nextOpeningTime)


                switch (forwardOrdering) {
                    case 'never':
                        storeIsOpen.value = false
                        statusStore.value = {
                            class: STORE_CLOSE_CLASS,
                            text: getStoreCloseText()
                        }
                        break
                    case 'before_closing':
                        if (isOutsideOpeningHours) {
                            if (
                                nextOpeningTimeNumber &&
                                currentTime < closeTimeDay &&
                                currentTime < nextOpeningTimeNumber!
                            ) {
                                storeIsOpen.value = true
                                statusStore.value = {
                                    class: STORE_OPEN_SOON_CLASS,
                                    text: getStoreOrderOpenOpensAtText(nextOpeningTime)

                                }
                                return
                            } else if (nextDayOpening) {
                                storeIsOpen.value = false
                                statusStore.value = {
                                    class: STORE_CLOSE_CLASS,
                                    text: textNextOpen
                                }
                                return
                            } else {
                                storeIsOpen.value = false
                                statusStore.value = {
                                    class: STORE_IS_OPEN_CLASS,
                                    text: getStoreIsOpenText()
                                }
                                return
                            }
                        } else {
                            storeIsOpen.value = false
                            statusStore.value = {
                                class: STORE_IS_OPEN_CLASS,
                                text: getStoreOpenOrderClosedText()
                            }
                            return
                        }
                    case 'after_opening':
                        if (isInsideOpeningHours) {
                            storeIsOpen.value = true
                            statusStore.value = {
                                class: STORE_IS_OPEN_CLASS,
                                text: getStoreIsOpenText()
                            }
                        } else {
                            storeIsOpen.value = false
                            if (nextOpeningPeriod) {
                                statusStore.value = {
                                    class: STORE_OPEN_SOON_CLASS,
                                    text: getStoreOpensAtText(nextOpeningPeriod.open)
                                }
                                return
                            } else if (nextDayOpening) {
                                statusStore.value = {
                                    class: STORE_CLOSE_CLASS,
                                    text:
                                        getStoreClosedOpensOnText(nextOpeningDayName, nextOpeningTime)
                                }
                                return
                            }

                        }
                        break
                    case 'anytime':
                        if (currentTime < closeTimeDay) {
                            if (isInsideOpeningHours) {
                                storeIsOpen.value = true
                                statusStore.value = {
                                    class: STORE_IS_OPEN_CLASS,
                                    text: getStoreIsOpenText()
                                }
                                return
                            } else {
                                if (nextOpeningPeriod) {
                                    storeIsOpen.value = true
                                    statusStore.value = {
                                        class: STORE_OPEN_SOON_CLASS,
                                        text: getStoreOrderOpenOpensAtText(nextOpeningPeriod.open)
                                    }
                                    return
                                } else {
                                    storeIsOpen.value = false
                                    statusStore.value = {
                                        class: STORE_CLOSE_CLASS,
                                        text: textNextOpen
                                    }
                                    return
                                }
                            }
                        } else {
                            if (nextDayOpening) {
                                storeIsOpen.value = false
                                statusStore.value = {
                                    class: STORE_CLOSE_CLASS,
                                    text:
                                        getStoreClosedOpensOnText(nextOpeningDayName, nextOpeningTime)
                                }
                                return
                            } else {
                                storeIsOpen.value = false
                                statusStore.value = {
                                    class: STORE_CLOSE_CLASS,
                                    text: getStoreCloseText()
                                }
                            }
                        }
                        return
                }
            } else if (instantOrdering === '1' && forwardOrdering) {
                if (nextOpeningPeriod) {
                    nextOpeningTimeNumber = getMinutesByData(
                        nextOpeningPeriod.open
                    )
                    nextOpeningTime = nextOpeningPeriod.open
                } else if (nextDayOpening) {
                    nextOpeningTime = nextDayOpening.open
                    nextOpeningTimeNumber = getMinutesByData(
                        nextDayOpening.open
                    )
                } else {
                    nextOpeningTime = null
                    nextOpeningTimeNumber = null
                }

                const textNextOpen = getStoreClosedOpensOnText(nextOpeningDayName, nextOpeningTime)

                switch (forwardOrdering) {
                    case 'never':
                        if (!isInsideOpeningHours) {
                            if (nextOpeningPeriod) {
                                statusStore.value = {
                                    class: STORE_OPEN_SOON_CLASS,
                                    text: getStoreOpensAtText(nextOpeningPeriod.open)
                                }
                                return
                            } else if (nextDayOpening) {
                                statusStore.value = {
                                    class: STORE_CLOSE_CLASS,
                                    text:
                                        getStoreClosedOpensOnText(nextOpeningDayName, nextOpeningTime)
                                }
                                return
                            }

                            storeIsOpen.value = false
                            statusStore.value = {
                                class: STORE_CLOSE_CLASS,
                                text: getStoreCloseText()
                            }
                            return
                        } else if (isInsideOpeningHours) {
                            storeIsOpen.value = true
                            statusStore.value = {
                                class: STORE_IS_OPEN_CLASS,
                                text: getStoreIsOpenText()
                            }
                            return
                        }
                        break
                    case 'before_closing':
                        if (isOutsideOpeningHours) {
                            if (nextOpeningTimeNumber &&
                                currentTime < closeTimeDay &&
                                currentTime < nextOpeningTimeNumber!) {
                                storeIsOpen.value = true
                                statusStore.value = {
                                    class: STORE_OPEN_SOON_CLASS,
                                    text: getStoreOrderOpenOpensAtText(nextOpeningTime)
                                }
                                return
                            } else if (nextDayOpening) {
                                storeIsOpen.value = false
                                statusStore.value = {
                                    class: STORE_CLOSE_CLASS,
                                    text: textNextOpen
                                }
                                return
                            } else {
                                storeIsOpen.value = true
                                statusStore.value = {
                                    class: STORE_IS_OPEN_CLASS,
                                    text: getStoreIsOpenText()
                                }
                                return
                            }
                        } else {
                            storeIsOpen.value = true
                            statusStore.value = {
                                class: STORE_IS_OPEN_CLASS,
                                text: getStoreIsOpenText()
                            }
                            return
                        }
                    case 'after_opening':
                        if (isInsideOpeningHours) {
                            storeIsOpen.value = true
                            statusStore.value = {
                                class: STORE_IS_OPEN_CLASS,
                                text: getStoreIsOpenText()
                            }
                        } else {
                            if (nextOpeningPeriod) {
                                storeIsOpen.value = false
                                statusStore.value = {
                                    class: STORE_OPEN_SOON_CLASS,
                                    text: getStoreOpensAtText(nextOpeningPeriod.open)
                                }
                                return
                            } else if (nextDayOpening) {
                                statusStore.value = {
                                    class: STORE_CLOSE_CLASS,
                                    text:
                                        getStoreClosedOpensOnText(nextOpeningDayName, nextOpeningTime)
                                }
                                return
                            }
                        }
                        break
                    case 'anytime':
                        if (currentTime < closeTimeDay) {
                            if (isInsideOpeningHours) {
                                storeIsOpen.value = true
                                statusStore.value = {
                                    class: STORE_IS_OPEN_CLASS,
                                    text: getStoreIsOpenText()
                                }
                                return
                            } else {
                                if (nextOpeningPeriod) {
                                    storeIsOpen.value = true
                                    statusStore.value = {
                                        class: STORE_OPEN_SOON_CLASS,
                                        text: getStoreOrderOpenOpensAtText(nextOpeningPeriod.open)
                                    }
                                    return
                                } else {
                                    storeIsOpen.value = false
                                    statusStore.value = {
                                        class: STORE_CLOSE_CLASS,
                                        text: textNextOpen
                                    }
                                    return
                                }
                            }
                        } else {
                            if (nextDayOpening) {
                                storeIsOpen.value = false
                                statusStore.value = {
                                    class: STORE_CLOSE_CLASS,
                                    text: getStoreClosedOpensOnText(nextOpeningDayName, nextOpeningTime)
                                }
                                return
                            } else {
                                storeIsOpen.value = false
                                statusStore.value = {
                                    class: STORE_CLOSE_CLASS,
                                    text: getStoreCloseText()
                                }
                                return
                            }
                        }
                }
            }
        }
    } else {
        storeIsOpen.value = false
        statusStore.value = {
            class: STORE_CLOSE_CLASS,
            text: getStoreCloseText()
        }
    }


}
